import React, { FC, Fragment, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import { Box, Button, Divider, Typography, Unstable_Grid2 as Grid } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';

import { CreateClientInvitationInput, useCreateClientInvitationMutation } from 'api';
import { FeedSelect } from 'features/feed';
import { InviteInput, Layouts, Loaders, PaddedPaper, Video } from 'components';
import { useNotify } from 'hooks';
import { getAppImage } from 'helpers';
import * as paths from 'paths';

import { WORKSPACE_VIDEO } from '../constants';
import {
  SetupInformation,
  SetupProfileButton,
  SetupSocials,
  SetupWorkspaceButton,
} from '../components';
import { clientInvitesSchema } from '../validations';

const styles = {
  divider: {
    marginTop: 7,
    marginBottom: 4,
  },
  root: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: 'primary.main',
    border: `2px solid primary.main`,
    borderRadius: '50%',
    width: 4.5,
    height: 4.5,
  },
} as const;

const SetupWorkspace: FC = () => {
  const notify = useNotify();
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>();
  const { t } = useTranslation();

  const initialValues: CreateClientInvitationInput = {
    email_addresses: [],
  };

  const { mutate: sendInvites, isLoading } = useCreateClientInvitationMutation({
    onError: notify.mutationError,
    onSuccess: () => {
      notify.info({ message: t('invites:inviteSuccess') });
    },
  });

  function handleCreatePost() {
    navigate(paths.postCreate);
  }

  function handleSubmit(values: CreateClientInvitationInput) {
    return sendInvites({ data: values });
  }

  function renderSidebar() {
    return (
      <FeedSelect>
        <SetupWorkspaceButton />
        <SetupProfileButton />
      </FeedSelect>
    );
  }

  function renderMain() {
    return (
      <PaddedPaper padding="48px 24px" sx={{ border: 0 }}>
        <Grid container>
          <Grid xs>
            <Typography variant="h2">{t('setup:setupWorkspaceHeader')}</Typography>
            <Typography variant="h5">{t('setup:setupWorkspaceSubHead')}</Typography>
          </Grid>
          <Grid>
            <img width={350} {...getAppImage('illustrations/thinking.png')} />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid>
            <Box sx={styles.root}>
              <Typography color="primary" variant="h6">
                1
              </Typography>
            </Box>
          </Grid>
          <Grid>
            <Typography variant="h4">{t('setup:setupProfileStep1Header')}</Typography>
            <Typography color="textSecondary">{t('setup:setupProfileStep1SubHead')}</Typography>
            <Box marginTop={3}>
              <Video ref={videoRef} disableHeight video={WORKSPACE_VIDEO} />
            </Box>
          </Grid>
        </Grid>
        <Divider sx={styles.divider} />
        <Grid container spacing={2}>
          <Grid>
            <Box sx={styles.root}>
              <Typography color="primary" variant="h6">
                2
              </Typography>
            </Box>
          </Grid>
          <Grid>
            <Typography variant="h4">{t('setup:setupProfileStep2Header')}</Typography>
            <Typography color="textSecondary">{t('setup:setupProfileStep2SubHead')}</Typography>
            <SetupInformation />
          </Grid>
        </Grid>
        <Divider sx={styles.divider} />
        <Grid container spacing={2}>
          <Grid>
            <Box sx={styles.root}>
              <Typography color="primary" variant="h6">
                3
              </Typography>
            </Box>
          </Grid>
          <Grid>
            <Typography variant="h4">{t('setup:setupProfileStep3Header')}</Typography>
            <Typography color="textSecondary">{t('setup:setupProfileStep3SubHead')}</Typography>
            <SetupSocials />
          </Grid>
        </Grid>
        <Divider sx={styles.divider} />
        <Grid container spacing={2}>
          <Grid>
            <Box sx={styles.root}>
              <Typography color="primary" variant="h6">
                4
              </Typography>
            </Box>
          </Grid>
          <Grid>
            <Typography variant="h4">{t('setup:setupWorkspaceStep4Header')}</Typography>
            <Typography paragraph color="textSecondary">
              {t('setup:setupWorkspaceStep4SubHead')}
            </Typography>
            <Box marginTop={3}>
              <Button onClick={handleCreatePost} startIcon={<EditIcon />} variant="contained">
                {t('setup:setupWorkspacePostButton')}
              </Button>
            </Box>
          </Grid>
        </Grid>
        <Divider sx={styles.divider} />
        <Grid container spacing={2}>
          <Grid>
            <Box sx={styles.root}>
              <Typography color="primary" variant="h6">
                5
              </Typography>
            </Box>
          </Grid>
          <Grid>
            <Typography variant="h4">{t('setup:setupWorkspaceStep2Header')}</Typography>
            <Typography color="textSecondary">{t('setup:setupWorkspaceStep2SubHead')}</Typography>
            <Box marginTop={3}>
              <Formik
                initialValues={initialValues}
                onSubmit={handleSubmit}
                validationSchema={clientInvitesSchema}
              >
                {({ submitForm }) => (
                  <Fragment>
                    <InviteInput />
                    <Box paddingTop={2}>
                      <Button
                        onClick={submitForm}
                        disabled={isLoading}
                        type="submit"
                        variant="contained"
                        color="primary"
                      >
                        {isLoading ? <Loaders.Standard height={24} /> : t('common:send')}
                      </Button>
                    </Box>
                  </Fragment>
                )}
              </Formik>
            </Box>
          </Grid>
        </Grid>
      </PaddedPaper>
    );
  }

  return <Layouts.App2Column mainWidth="standard" sidebar={renderSidebar()} main={renderMain()} />;
};

export default SetupWorkspace;
